import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Best-effort report al backend errors.ts. Si falla (red caída,
        // backend off), no escalamos: ya estamos en un fallback de error.
        try {
            const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'https://giti-backend.daguilera.workers.dev';
            fetch(`${apiBase}/api/errors`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kind: 'react.error_boundary',
                    message: error.message,
                    stack: error.stack || null,
                    component_stack: info.componentStack || null,
                    url: window.location.href,
                    user_agent: navigator.userAgent,
                }),
            }).catch(() => {});
        } catch {}
    }

    handleReset = () => {
        this.setState({ error: null });
    };

    render() {
        if (!this.state.error) return this.props.children;
        if (this.props.fallback) return this.props.fallback;

        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
                <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="text-4xl mb-4" aria-hidden="true">⚠️</div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Algo salió mal
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        La aplicación encontró un error inesperado. El equipo ya fue notificado.
                    </p>
                    <details className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        <summary className="cursor-pointer">Detalles técnicos</summary>
                        <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded overflow-auto whitespace-pre-wrap break-words">
                            {this.state.error.message}
                        </pre>
                    </details>
                    <div className="flex gap-2">
                        <button
                            onClick={this.handleReset}
                            className="flex-1 px-4 py-2 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-blue-dark transition-colors"
                        >
                            Reintentar
                        </button>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Inicio
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
